import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { aboutInfo } from '@/mocks/aboutData';

const schemaData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.douhuavideo.com/' },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.douhuavideo.com/about' },
      ],
    },
    {
      '@type': 'Organization',
      name: '豆花视频',
      url: 'https://www.douhuavideo.com',
      description: '豆花视频是正版高清视频平台，致力于为用户提供优质的视频内容服务。',
      email: 'contact@douhuavideo.com',
      telephone: '+86-400-888-6688',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '望京科技园豆花大厦A座',
        addressLocality: '北京市',
        addressRegion: '朝阳区',
        addressCountry: 'CN',
      },
      sameAs: [
        'https://weibo.com/douhuavideo',
        'https://www.douyin.com/douhuavideo',
      ],
    },
    {
      '@type': 'AboutPage',
      name: '关于豆花视频',
      description: '了解豆花视频的品牌故事、发展历程、用户口碑和联系方式。',
      url: 'https://www.douhuavideo.com/about',
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: '豆花视频是什么时候成立的？',
          acceptedAnswer: { '@type': 'Answer', text: '豆花视频成立于2020年，是一家专注于正版高清视频内容的在线平台。' },
        },
        {
          '@type': 'Question',
          name: '豆花视频有多少用户？',
          acceptedAnswer: { '@type': 'Answer', text: '豆花视频累计注册用户超过5000万，日活跃用户超过1500万。' },
        },
        {
          '@type': 'Question',
          name: '豆花视频的联系方式是什么？',
          acceptedAnswer: { '@type': 'Answer', text: '您可以通过邮箱contact@douhuavideo.com或电话400-888-6688与我们联系。办公地址位于北京市朝阳区望京科技园豆花大厦A座。' },
        },
      ],
    },
  ],
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('phone_alt') as string || '').trim();

    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('idle');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9qm654nlsngrm4lm1sg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }

      if (res.ok && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        const msg = (parsed as { meta?: { message?: string } }).meta?.message || (parsed as { message?: string }).message || '提交失败，请稍后重试';
        setFormError(msg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <Navbar />
      <main className="pt-16">
        {/* Breadcrumb */}
        <nav className="w-full px-4 md:px-6 py-4 bg-background-50 border-b border-background-200/70" aria-label="面包屑导航">
          <ol className="flex items-center gap-2 text-sm text-foreground-500">
            <li><Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li className="text-foreground-900 font-medium">关于我们</li>
          </ol>
        </nav>

        {/* Brand Introduction */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
                {aboutInfo.brand.title}
              </h1>
              <p className="text-base text-foreground-500 font-heading">{aboutInfo.brand.subtitle}</p>
            </div>

            <div className="space-y-5">
              {aboutInfo.brand.description.map((para, idx) => (
                <p key={idx} className="text-base text-foreground-700 leading-relaxed">
                  {para}
                </p>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-12">
              发展历程
            </h2>
            <div className="space-y-0">
              {aboutInfo.milestones.map((milestone, idx) => (
                <div key={idx} className="flex gap-4 pb-8 relative">
                  {idx < aboutInfo.milestones.length - 1 && (
                    <div className="absolute left-[19px] top-10 bottom-0 w-px bg-background-300"></div>
                  )}
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center">
                    <i className="ri-flag-line text-white text-sm"></i>
                  </div>
                  <div className="pt-2">
                    <span className="text-sm font-bold text-accent-600">{milestone.year}</span>
                    <p className="text-sm text-foreground-600 mt-1">{milestone.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
                {aboutInfo.reputation.title}
              </h2>
              <p className="text-sm text-foreground-500">{aboutInfo.reputation.subtitle}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {aboutInfo.reputation.reviews.map((review, idx) => (
                <div key={idx} className="p-5 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-sm font-bold text-primary-600">{review.avatar}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{review.name}</p>
                      <p className="text-xs text-foreground-400">{review.date}</p>
                    </div>
                  </div>
                  <div className="flex gap-0.5 mb-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className={`ri-star-fill text-xs ${i < review.rating ? 'text-accent-500' : 'text-foreground-300'}`}></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="w-full py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
                {aboutInfo.contact.title}
              </h2>
              <p className="text-sm text-foreground-500">{aboutInfo.contact.subtitle}</p>
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
              {/* Contact Info */}
              <div className="flex-1 space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-mail-line text-primary-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-phone-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">客服电话</p>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-map-pin-line text-secondary-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">公司地址</p>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-time-line text-primary-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">工作时间</p>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.workingHours}</p>
                  </div>
                </div>

                {/* Map */}
                <div className="rounded-xl overflow-hidden border border-background-200/70 mt-4">
                  <iframe
                    src={aboutInfo.contact.mapEmbedUrl}
                    width="100%"
                    height="220"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="豆花视频公司位置"
                    aria-label="豆花视频公司位置地图"
                  ></iframe>
                </div>
              </div>

              {/* Contact Form */}
              <div className="flex-1">
                <form onSubmit={handleSubmit} className="bg-background-50 rounded-xl p-6 border border-background-200/70" data-readdy-form="">
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-5">发送消息</h3>

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        id="about-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>

                    <div>
                      <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        id="about-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>

                    <div>
                      <label htmlFor="about-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                      <input
                        id="about-subject"
                        name="subject"
                        type="text"
                        required
                        className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入消息主题"
                      />
                    </div>

                    <div>
                      <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">消息内容</label>
                      <textarea
                        id="about-message"
                        name="message"
                        rows={4}
                        required
                        maxLength={500}
                        className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                        placeholder="请输入您想说的话（最多500字）"
                      ></textarea>
                    </div>

                    {/* Honeypot */}
                    <input
                      name="phone_alt"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />

                    <button
                      type="submit"
                      className="w-full py-3 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                    >
                      发送消息
                    </button>
                  </div>

                  {formStatus === 'success' && (
                    <p className="mt-4 text-sm text-secondary-600 bg-secondary-50 rounded-lg px-4 py-3">
                      消息已发送成功！我们会尽快与您联系。
                    </p>
                  )}
                  {formStatus === 'error' && (
                    <p className="mt-4 text-sm text-accent-600 bg-accent-50 rounded-lg px-4 py-3">
                      {formError}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}